/**
 * Rutas admin que saturaban Hyperdrive en el Worker.
 * El cliente va directo a Railway (pg pool); el Worker también proxea estas rutas.
 */

export const HYPERDRIVE_ADMIN_PATHS = new Set([
  "/api/admin/wishlist-config",
  "/api/admin/geo-neighborhood-config",
  "/api/admin/geo-traffic-stats",
  "/api/admin/country-template-config",
  "/api/admin/mejores-amigos-config",
  "/api/admin/geo-cities-ar",
]);

/** Admin + Cloudinary servidos en Railway (pg pool / secrets). Worker solo proxea. */
export const RAILWAY_FIRST_ADMIN_PATHS = new Set([
  "/api/admin/posthog-geo-traffic",
  /** Live experience hub + DualCTA variant leaderboard (PostHog HogQL + pg). */
  "/api/admin/visitor-experience-live",
  "/api/admin/visitor-experience-journeys",
  "/api/admin/dual-cta-variant-performance",
  "/api/admin/dual-cta-variant-recommendations",
  "/api/admin/dual-cta-ops-config",
  "/api/admin/ingresantes-pattern-analysis",
  /**
   * Dual CTA Video Templates (vip_video_assets) — ensure PROMO coupons + Stripe
   * price validation. Worker CF-native returns 501 not_implemented.
   */
  "/api/admin/vip-video-assets",
  /** Video A/B slots — pg + ensure PROMO on save. Prefer Railway over Worker. */
  "/api/admin/video-ab-slots",
  "/api/admin/site-settings",
  "/api/admin/integrations-health",
  "/api/admin/config-health",
  "/api/admin/portfolio",
  /**
   * Watermark iPhone — herramienta web + atajo opcional + process photos.
   * Worker CF-native returns 501 not_implemented without this list entry.
   */
  "/api/admin/watermark-shortcut",
  "/api/admin/watermark-process",
  // Media kit: same-origin /api/admin/mediakit → Worker proxies to Railway → Postgres
  // (media_kits). Do NOT put in Railway-first — browser stays on www; data is still pg.
  "/api/admin/feature-access",
  "/api/admin/finanzas-personales",
  /** Full ARS line items + 30d contract — Worker CF-native was a reduced stub. */
  "/api/admin/revenue-summary",
  "/api/admin/send-revenue-summary-slack",
  "/api/admin/send-revenue-email",
  "/api/admin/send-weekly-revenue-email",
  "/api/admin/mercadopago-collection-accounts",
  "/api/admin/audit-log",
  "/api/admin/memberships/extend",
  "/api/admin/memberships/revoke",
  "/api/admin/memberships/unsuspend",
  "/api/admin/memberships/send-access-link",
  "/api/admin/memberships/send-validation-email",
  "/api/admin/memberships/send-pending-hold-email",
  "/api/admin/memberships/lifecycle-recovery",
  "/api/admin/memberships/send-broadcast",
  /** Campaña Argentina ganó + WINNER — Vercel/Railway (Stripe checkout + Resend). */
  "/api/admin/campaigns/argentina-win",
  "/api/admin/ga4-measurement-protocol-health",
  "/api/admin/photo-analytics",
  "/api/admin/media-kit-analytics",
  "/api/admin/web-templates-analytics",
  "/api/admin/mux-analytics",
  "/api/admin/mux-conversion-report",
  "/api/admin/conversion-health-report",
  /**
   * Orquestador AR Kimi (settings + PostHog metrics + probe).
   * Worker CF-native returns 501 not_implemented without this list entry.
   */
  "/api/admin/conversion-orchestrator",
  "/api/admin/reels/config",
  "/api/admin/reels/share-links",
  "/api/admin/reels/feed-preview",
  "/api/admin/reels/upload",
  "/api/admin/reels/videos",
  "/api/admin/reel-invitations",
  /** One-time Instagram share links for /invitacion-premium?ot= — Railway pg. */
  "/api/admin/premium-invite-share-links",
  "/api/cloudinary/gallery",
  "/api/cloudinary/upload",
  "/api/cloudinary/ensure-preset",
]);

/** Lecturas públicas del panel CTA que también pegaban Hyperdrive en el Worker. */
export const ADMIN_SERIALIZED_PUBLIC_PATHS = new Set([
  "/api/wishlist-config-neighborhood",
  "/api/geo-cities-ar-public",
]);

export function isHyperdriveAdminPath(path: string): boolean {
  const p = path.split("?")[0];
  return HYPERDRIVE_ADMIN_PATHS.has(p);
}

export function isRailwayFirstAdminPath(path: string): boolean {
  const p = path.split("?")[0];
  return RAILWAY_FIRST_ADMIN_PATHS.has(p) || HYPERDRIVE_ADMIN_PATHS.has(p);
}

export function isAdminSerializedPublicPath(path: string): boolean {
  const p = path.split("?")[0];
  return ADMIN_SERIALIZED_PUBLIC_PATHS.has(p);
}

export function shouldSerializeAdminFetch(path: string): boolean {
  return isHyperdriveAdminPath(path) || isAdminSerializedPublicPath(path);
}

/** Origen Railway — API directa (evita doble salto Worker→Hyperdrive). */
export function getRailwayAdminApiOrigin(): string | null {
  const fromEnv = (import.meta.env.VITE_RAILWAY_API_ORIGIN as string | undefined)?.trim();
  if (fromEnv) return fromEnv.replace(/\/$/, "");
  if (import.meta.env.DEV) return null;
  return "https://delfucampo-main-production.up.railway.app";
}

/** Railway primero en producción; www (Worker) como fallback. */
export function buildHyperdriveAdminUrls(path: string): string[] {
  const railway = getRailwayAdminApiOrigin();
  if (railway && isRailwayFirstAdminPath(path)) {
    return [`${railway}${path}`, path];
  }
  return [path];
}

export function buildSerializedPublicUrls(path: string): string[] {
  const p = path.split("?")[0];
  const railway = getRailwayAdminApiOrigin();
  if (railway && ADMIN_SERIALIZED_PUBLIC_PATHS.has(p)) {
    return [`${railway}${path}`, path];
  }
  return [path];
}